import { isNetworkFailure, timedFetch } from './net';
/** Minimal DICOMweb client (QIDO-RS + WADO-RS metadata) for the ris gateway or any DICOMweb PACS. */

export type DicomJson = Record<string, { vr: string; Value?: unknown[] } | undefined>;

export interface InstanceRef {
  sopInstanceUID: string;
  instanceNumber: number;
  numberOfFrames: number;
  /** Full WADO-RS metadata (fast mode only); the wadors image loader needs it to decode frames. */
  metadata?: DicomJson;
}

export interface SeriesRef {
  studyInstanceUID: string;
  seriesInstanceUID: string;
  seriesNumber: number;
  modality: string;
  description: string;
  instanceCount: number;
  instances: InstanceRef[];
}

export interface StudyRef {
  studyInstanceUID: string;
  patientName: string;
  patientId: string;
  patientBirthDate: string;
  patientSex: string;
  studyDate: string;
  studyTime: string;
  studyDescription: string;
  accessionNumber: string;
  institutionName: string;
  series: SeriesRef[];
}

const QIDO_ACCEPT = 'application/dicom+json';

export class DicomWebClient {
  constructor(
    private readonly root: string,
    /** The viewer ticket, or how to read it at request time (it is renewed under a hub session). */
    private readonly token: string | null | (() => string | null),
    /** Extra headers on every request (parity mode adds IMEXHS's no-store / no-cache pair). */
    private readonly extraHeaders: Record<string, string> = {},
  ) {}

  /** Auth only: what the image loader needs on pixel requests. */
  headers(): Record<string, string> {
    const token = typeof this.token === 'function' ? this.token() : this.token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  requestHeaders(): Record<string, string> {
    return { ...this.extraHeaders, ...this.headers() };
  }

  /** QIDO / metadata GET; retried once when it got no answer (idle timeout, dropped connection). */
  private async getJson(path: string): Promise<DicomJson[]> {
    try {
      return await this.getJsonOnce(path);
    } catch (error) {
      if (!isNetworkFailure(error)) throw error;
      console.warn('[viewer] DICOMweb request got no answer, retrying once', path.split('?')[0], error);
      return this.getJsonOnce(path);
    }
  }

  private async getJsonOnce(path: string): Promise<DicomJson[]> {
    const response = await timedFetch(`${this.root}/${path}`, {
      headers: { Accept: QIDO_ACCEPT, ...this.requestHeaders() },
      credentials: 'same-origin',
    });
    if (response.status === 204) return [];
    if (!response.ok) throw new Error(`DICOMweb ${response.status} for ${path.split('?')[0]}`);
    return (await response.json()) as DicomJson[];
  }

  async findStudy(studyUID: string): Promise<DicomJson | undefined> {
    const rows = await this.getJson(
      `studies?StudyInstanceUID=${encodeURIComponent(studyUID)}&includefield=00081030,00080090,00080080,00100030,00100040,00080050,00080020,00080030`,
    );
    return rows[0];
  }

  listSeries(studyUID: string): Promise<DicomJson[]> {
    return this.getJson(`studies/${studyUID}/series?includefield=0008103E,00200011,00080060,00201209`);
  }

  listInstances(studyUID: string, seriesUID: string): Promise<DicomJson[]> {
    return this.getJson(`studies/${studyUID}/series/${seriesUID}/instances?includefield=00200013,00280008`);
  }

  seriesMetadata(studyUID: string, seriesUID: string): Promise<DicomJson[]> {
    return this.getJson(`studies/${studyUID}/series/${seriesUID}/metadata`);
  }

  /**
   * Bulk data behind a metadata BulkDataURI, re-rooted on this client's root (PACS answer with their own
   * internal host, which the browser must not reach). Undefined when the URI is not an instance's bulk
   * data path (see rerootBulkDataUri).
   */
  async bulk(uri: string, accept: string): Promise<{ bytes: Uint8Array; contentType: string } | undefined> {
    const url = rerootBulkDataUri(this.root, uri);
    if (!url) return undefined;
    const response = await timedFetch(url, {
      headers: { Accept: accept, ...this.requestHeaders() },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`DICOMweb ${response.status} for bulk data`);
    return { bytes: new Uint8Array(await response.arrayBuffer()), contentType: response.headers.get('Content-Type') ?? '' };
  }

  instanceUrl(series: SeriesRef, instance: InstanceRef): string {
    return `${this.root}/studies/${series.studyInstanceUID}/series/${series.seriesInstanceUID}/instances/${instance.sopInstanceUID}`;
  }

  frameUrl(series: SeriesRef, instance: InstanceRef, frame: number): string {
    return `${this.root}/studies/${series.studyInstanceUID}/series/${series.seriesInstanceUID}/instances/${instance.sopInstanceUID}/frames/${frame}`;
  }
}

/**
 * An instance-level bulk data path inside the study tree, and nothing else: UIDs are digits and dots,
 * the tail is plain path segments (Orthanc `/bulk/00283010/0/00283006`, dcm4chee `/bulkdata/…`), with
 * an optional plain query (offset / length).
 */
const BULK_PATH = /^\/studies\/[0-9.]+\/series\/[0-9.]+\/instances\/[0-9.]+\/[A-Za-z0-9_.-]+(?:\/[A-Za-z0-9_.-]+)*(?:\?[A-Za-z0-9_.=&-]*)?$/;

/**
 * A metadata BulkDataURI re-rooted on `root`, or undefined when it is anything but an instance's bulk
 * data path. The URI comes from the PACS (ultimately from whatever object was stored), and the request
 * carries the viewer ticket: a crafted `/studies/../../<other route>` must never be followed, so `..`
 * segments and percent-encoding are refused outright.
 */
export function rerootBulkDataUri(root: string, uri: string): string | undefined {
  const at = uri.indexOf('/studies/');
  if (at < 0) return undefined;
  const path = uri.slice(at);
  if (path.includes('%') || path.split(/[/?]/).some((segment) => segment === '..' || segment === '.')) return undefined;
  return BULK_PATH.test(path) ? `${root}${path}` : undefined;
}

export function tagValue(json: DicomJson | undefined, tag: string): unknown {
  return json?.[tag]?.Value?.[0];
}

export function tagString(json: DicomJson | undefined, tag: string): string {
  const value = tagValue(json, tag);
  if (value === undefined || value === null) return '';
  if (typeof value === 'object' && value !== null && 'Alphabetic' in value) {
    return String((value as { Alphabetic: string }).Alphabetic ?? '');
  }
  return String(value);
}

export function tagNumber(json: DicomJson | undefined, tag: string, fallback = 0): number {
  const n = Number(tagValue(json, tag));
  return Number.isFinite(n) ? n : fallback;
}
